import { loadNutrientViewer } from "utilities/nutrient/nutrient.utility";

export interface IFlattenPlanAnnotationsOptions {
    licenseKey: string | undefined;
    documentUrl: string;
    /** InstantJSON payload as a JSON string. Used when xfdf is not provided. */
    instantJSON?: string;
    /** XFDF payload. Preferred over instantJSON when both are present. */
    xfdf?: string;
    /** Aborts the load + export if it exceeds this many ms. Defaults to 60 seconds. */
    timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 60_000;

function getBaseUrl(): string {
    return `${window.location.protocol}//${window.location.host}${import.meta.env.BASE_URL}`;
}

/**
 * Loads a plan-page PDF + its annotation payload in Nutrient's headless mode,
 * exports a flattened copy as a Blob, then disposes the instance. Used by the
 * version-history download flow so we can produce an annotated PDF for any
 * version (including ones not currently displayed) without disturbing the
 * user's active viewer. Headless mode skips the UI entirely — no DOM container
 * is needed, which is faster than mounting and tearing down toolbars/sidebars
 * that we'd never see.
 *
 * Nutrient's .load() still briefly mutates document.body (prepends an
 * aria-live region for screen-reader announcements), which would normally
 * make the page scrollbar flicker. PlanPageDetails suppresses that by setting
 * `overflow: hidden` on document.documentElement while the viewer is
 * mounted, so the flatten doesn't cause a layout shift.
 */
export async function flattenPlanAnnotations({
    licenseKey,
    documentUrl,
    instantJSON,
    xfdf,
    timeoutMs = DEFAULT_TIMEOUT_MS,
}: IFlattenPlanAnnotationsOptions): Promise<Blob> {
    const NutrientViewer = await loadNutrientViewer();

    let instance: Awaited<ReturnType<typeof NutrientViewer.load>> | null = null;
    let timeoutHandle: number | null = null;

    try {
        // The @nutrient-sdk/viewer TS types model `container` as required on
        // StandaloneConfiguration and don't have a dedicated headless variant
        // that drops it. The runtime accepts (and the Nutrient docs require)
        // omitting `container` when `headless: true` — passing it is
        // explicitly called out as not making sense. Build the config as a
        // permissive shape and assert to the SDK's expected type to bridge
        // that gap.
        const config = {
            licenseKey,
            headless: true,
            document: documentUrl,
            baseUrl: getBaseUrl(),
            autoSaveMode: NutrientViewer.AutoSaveMode.DISABLED,
        } as Parameters<typeof NutrientViewer.load>[0] & {
            XFDF?: string;
            instantJSON?: unknown;
        };

        // Annotation payload: prefer XFDF when available (modern path), fall
        // back to InstantJSON (legacy rows). Mirrors NutrientPlanViewer.tsx.
        if (xfdf) {
            config.XFDF = xfdf;
        } else if (instantJSON) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const parsedInstantJSON = JSON.parse(instantJSON);
            // pdfId mismatches between the stored document provider and the
            // freshly loaded PDF cause Nutrient to reject the import. Strip it.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            delete parsedInstantJSON.pdfId;
            config.instantJSON = parsedInstantJSON;
        }

        const timeoutPromise = new Promise<never>((_, reject) => {
            timeoutHandle = window.setTimeout(() => {
                reject(new Error(`flattenPlanAnnotations timed out after ${timeoutMs}ms`));
            }, timeoutMs);
        });

        instance = await Promise.race([NutrientViewer.load(config), timeoutPromise]);

        const buffer = await Promise.race([instance.exportPDF({ flatten: true }), timeoutPromise]);

        return new Blob([buffer], { type: "application/pdf" });
    } finally {
        if (timeoutHandle !== null) {
            window.clearTimeout(timeoutHandle);
        }
        if (instance) {
            try {
                NutrientViewer.unload(instance);
            } catch {
                // Best effort — if Nutrient throws on unload (already unloaded,
                // etc.) there's nothing else to clean up since headless mode
                // doesn't own any DOM nodes.
            }
        }
    }
}
